import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Users, 
  Calendar, 
  BookOpen,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';
import api  from '../components/api';

const CreateBulletin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState('');
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les années académiques
      const anneesResponse = await api.get('/etablissement/annees-academiques/');
      setAnnees(anneesResponse.data);
      
      // Récupérer l'année active par défaut
      const anneeActive = anneesResponse.data.find(a => a.status) || anneesResponse.data[0];
      if (anneeActive) {
        setSelectedAnnee(anneeActive.id);
      }
      
      // Récupérer les classes
      const classesResponse = await api.get('/classe/get-all-classe/');
      setClasses(classesResponse.data || []);
      
      // Récupérer les semestres
      const semestresResponse = await api.get('/emplois/semestres/actifs/');
      setSemestres(semestresResponse.data || []);
      
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur fetchData:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedClasse || !selectedSemestre || !selectedAnnee) {
      setError('Veuillez sélectionner tous les champs requis');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      // 1. D'abord valider que tous les étudiants ont des notes
      const validationResponse = await api.post('/note/valider-notes-avant-bulletin/', {
        classe_id: selectedClasse,
        semestre_id: selectedSemestre,
        annee_academique_id: selectedAnnee
      });

      if (!validationResponse.data.success || !validationResponse.data.validation_passed) {
        // Afficher les erreurs de validation de manière détaillée
        const details = validationResponse.data.details || [];
        if (details.length > 0) {
          const errorMessage = `Des étudiants n'ont pas de notes dans certaines évaluations:\n\n` +
            details.slice(0, 5).map(err => `• ${err.message}`).join('\n') +
            (details.length > 5 ? `\n... et ${details.length - 5} autres erreurs` : '');
          
          setError(errorMessage);
        } else {
          setError(validationResponse.data.error || 'Validation des notes échouée');
        }
        return;
      }

      // 2. Si validation OK, procéder à la génération
      const response = await api.post('/note/generer-bulletins/', {
        classe_id: selectedClasse,
        semestre_id: selectedSemestre,
        annee_academique_id: selectedAnnee
      });

      if (response.data.success) {
        setSuccess(`Bulletins générés avec succès pour ${response.data.count || 0} étudiants`);
        // Rediriger vers la page des bulletins après 2 secondes
        setTimeout(() => {
          navigate('/bulletins-notes');
        }, 2000);
      } else {
        setError(response.data.error || 'Erreur lors de la génération des bulletins');
      }
    } catch (err) {
      console.error('Erreur generate:', err);
      
      // Gérer les erreurs de validation
      if (err.response?.data?.details) {
        const details = err.response.data.details;
        const errorMessage = `Des étudiants n'ont pas de notes dans certaines évaluations:\n\n` +
          details.slice(0, 5).map(detail => `• ${detail.message}`).join('\n') +
          (details.length > 5 ? `\n... et ${details.length - 5} autres erreurs` : '');
        
        setError(errorMessage);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erreur lors de la génération des bulletins');
      }
    } finally {
      setGenerating(false);
    }
  };

  const isFormValid = selectedClasse && selectedSemestre && selectedAnnee;

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="w-full">
          
          {/* En-tête */}
          <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 rounded-xl p-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-white">
                <button
                  onClick={() => navigate('/bulletins-notes')}
                  className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="bg-white/10 p-3 rounded-xl mr-4">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Générer des Bulletins</h1>
                  <p className="mt-2 text-blue-100">Créez les bulletins pour une classe et un semestre spécifiques</p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-4 text-white/80">
                <div className="flex items-center">
                  <Sparkles className="h-5 w-5 mr-2" />
                  <span className="text-sm">Génération automatique</span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages d'état */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-red-700">
                  {error.includes('\n') ? (
                    <pre className="whitespace-pre-wrap font-sans text-sm">{error}</pre>
                  ) : (
                    <span>{error}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-green-700">{success}</span>
              </div>
            </div>
          )}

          {/* Formulaire principal */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            
            {/* En-tête du formulaire */}
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Paramètres de génération</h2>
              <p className="text-gray-600">Sélectionnez les critères pour générer les bulletins</p>
            </div>

            {/* Contenu du formulaire */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                
                {/* Sélection de l'année académique */}
                <div className="space-y-4">
                  <div className="flex items-center mb-3">
                    <Calendar className="h-5 w-5 text-[#202654] mr-2" />
                    <label className="text-sm font-medium text-gray-700">Année Académique</label>
                  </div>
                  <select
                    value={selectedAnnee}
                    onChange={(e) => setSelectedAnnee(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors"
                  >
                    <option value="">Sélectionner une année</option>
                    {annees.map((annee) => (
                      <option key={annee.id} value={annee.id}>
                        {annee.annee_debut}-{annee.annee_fin}
                        {annee.status ? ' (Active)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    L'année académique pour laquelle générer les bulletins
                  </p>
                </div>

                {/* Sélection de la classe */}
                <div className="space-y-4">
                  <div className="flex items-center mb-3">
                    <Users className="h-5 w-5 text-[#202654] mr-2" />
                    <label className="text-sm font-medium text-gray-700">Classe</label>
                  </div>
                  <select
                    value={selectedClasse}
                    onChange={(e) => setSelectedClasse(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors"
                  >
                    <option value="">Sélectionner une classe</option>
                    {classes.map((classe) => (
                      <option key={classe.id} value={classe.id}>
                        {classe.libelle} - {classe.niveau?.libelle}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    La classe pour laquelle générer les bulletins
                  </p>
                </div>

                {/* Sélection du semestre */}
                <div className="space-y-4">
                  <div className="flex items-center mb-3">
                    <BookOpen className="h-5 w-5 text-[#202654] mr-2" />
                    <label className="text-sm font-medium text-gray-700">Semestre</label>
                  </div>
                  <select
                    value={selectedSemestre}
                    onChange={(e) => setSelectedSemestre(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors"
                  >
                    <option value="">Sélectionner un semestre</option>
                    {semestres.map((semestre) => (
                      <option key={semestre.id} value={semestre.id}>
                        Semestre {semestre.numero}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    Le semestre pour lequel générer les bulletins
                  </p>
                </div>
              </div>

              {/* Informations supplémentaires */}
              <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200 max-w-6xl mx-auto">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Informations importantes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-blue-800">
                  <div className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Les moyennes seront automatiquement calculées</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Les bulletins seront générés pour tous les étudiants de la classe</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Les appréciations peuvent être ajoutées ultérieurement</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Les bulletins générés devront être validés</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="text-sm text-gray-600">
                  {isFormValid && (
                    <span className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                      Formulaire complet - Prêt à générer
                    </span>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={() => navigate('/bulletins-notes')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  
                  <button
                    onClick={handleGenerate}
                    disabled={!isFormValid || generating}
                    className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 flex items-center ${
                      isFormValid && !generating
                        ? 'bg-[#202654] text-white hover:bg-[#202654]/90 shadow-lg hover:shadow-xl'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Générer les Bulletins
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateBulletin;
